<template>
	<Dialog>
		<DialogContent :disable-overlay-close="disableOverlayClose" class="gap-1" @close="$emit('close')">
			<DialogTitle>Text-to-Speech</DialogTitle>
			<DialogDescription> Enter text to generate an audio file using text-to-speech. </DialogDescription>
			<div
				v-if="(tenants?.length > 1 || $global.hasUserRole(userRoles, 'reseller')) && !hideTenantSelector"
				class="formGroup py-2"
			>
				<div class="formGroup withIcon">
					<label class="textGrey">Tenant</label>
					<select v-model="selectedTenant" @change="selectedAccount = null">
						<option disabled :value="null">-- Select Tenant --</option>
						<option v-for="t in tenants" :key="t.id" :value="t.id">
							{{ t.companyName }}
						</option>
					</select>
					<span class="fa-light fa-chevron-down"></span>
				</div>
			</div>
			<div v-if="selectedTenant && !hideAccountSelector" class="formGroup py-2">
				<div class="formGroup withIcon">
					<label class="textGrey">Account</label>
					<select v-model="selectedAccount">
						<option :value="null">-- Unassigned - Tenant File --</option>
						<option v-for="a in tenantAccounts" :key="a.id" :value="a.id">
							{{ a.name }}
						</option>
					</select>
					<span class="fa-light fa-chevron-down"></span>
				</div>
			</div>
			<div v-if="selectedTenant" class="formGroup py-2">
				<label class="textGrey">Language</label>
				<select v-model="selectedLanguage" class="w-full rounded border p-2" @change="selectedVoice = null">
					<option v-for="language in languages" :key="language" :value="language">
						{{ language }}
					</option>
				</select>
			</div>
			<div v-if="selectedTenant && selectedLanguage" class="formGroup py-2">
				<label class="textGrey">Voice</label>
				<select v-model="selectedVoice" class="w-full rounded border p-2">
					<option disabled :value="null">-- Select Voice --</option>
					<option v-for="voice in languageVoices" :key="voice.name" :value="voice">
						{{ voice.displayName }}
					</option>
				</select>
			</div>
			<div v-if="selectedTenant && selectedLanguage && selectedVoice" class="formGroup py-2">
				<div class="flex items-center justify-between">
					<label class="textGrey">Enter Text</label>
					<div class="character-counter">
						<span
							:class="[
								'text-sm',
								isOverLimit
									? 'font-semibold text-red-500'
									: isNearLimit
										? 'font-medium text-orange-500'
										: 'text-gray-500'
							]"
						>
							{{ textInput.length }}/{{ maxCharacterLimit }}
						</span>
					</div>
				</div>
				<textarea
					v-model="textInput"
					rows="4"
					class="w-full rounded border p-2"
					:class="{ 'border-red-500': isOverLimit, 'border-orange-400': isNearLimit }"
					:maxlength="maxCharacterLimit + 100"
					placeholder="Enter the text you want to convert to speech..."
				></textarea>
				<div v-if="isOverLimit" class="mt-1 flex items-center gap-1 text-sm text-red-500">
					<span class="fa-solid fa-exclamation-triangle"></span>
					Text exceeds the {{ maxCharacterLimit }} character limit. Please shorten your text.
				</div>
				<div v-else-if="isNearLimit" class="mt-1 flex items-center gap-1 text-sm text-orange-500">
					<span class="fa-solid fa-exclamation-circle"></span>
					Approaching character limit. {{ maxCharacterLimit - textInput.length }} characters remaining.
				</div>
			</div>
			<div v-if="selectedTenant && selectedVoice?.supportsSpeed" class="formGroup py-2">
				<label class="textGrey">Speed</label>
				<input v-model="speed" type="range" min="0.25" max="4.0" step="0.01" class="w-full" />
				<span>{{ speed }}</span>
			</div>
			<div v-if="selectedTenant" class="formGroup py-2">
				<label class="textGrey">File Name</label>
				<input
					v-model="fileName"
					type="text"
					class="w-full rounded border p-2"
					placeholder="My Recording - YYYYMMDD_HHMMSS"
				/>
			</div>

			<div class="row gap-2 px-2 pt-2">
				<div v-if="isFileGenerated" class="w-full">
					<div class="overflow-hidden rounded-md p-4 pb-0">
						<media-player :file="file" />
					</div>
				</div>
				<div class="formGroup row gap-2 px-2">
					<button
						:disabled="
							!textInput ||
							!selectedTenant ||
							isLoading ||
							!selectedVoice ||
							!selectedLanguage ||
							isOverLimit ||
							!textInput.trim()
						"
						class="jmiBtn blue"
						@click="generateTTS"
					>
						{{ isLoading ? 'Generating...' : isFileGenerated ? 'Regenerate' : 'Generate' }}
					</button>
					<button
						v-if="isFileGenerated"
						:disabled="!selectedTenant || isLoading"
						class="jmiBtn green"
						@click="uploadFile"
					>
						{{ isLoading ? 'Uploading...' : 'Upload' }}
					</button>
				</div>
			</div>
		</DialogContent>
	</Dialog>
</template>

<script>
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import accountsApi from '@/scripts/accounts/accounts.api'
import filesApi from '@/scripts/files/files.api'

export default {
	components: {
		Dialog,
		DialogContent,
		DialogTitle,
		DialogDescription
	},
	inject: ['user', 'userRoles', 'tenants'],
	props: ['disableOverlayClose', 'selectedTenantId', 'selectedAccountId'],
	emits: ['close'],
	data() {
		return {
			accounts: [],
			selectedTenant: null,
			selectedAccount: null,
			isFileGenerated: false,
			previewedFile: null,
			hideTenantSelector: false,
			hideAccountSelector: false,
			isPlaying: false,
			currentTime: '00:00',
			file: null,
			totalDuration: '00:00',
			textInput: '',
			fileName: '',
			isLoading: false,
			selectedVoice: null,
			selectedLanguage: 'English - UK',
			speed: 1.0,
			voices: [],
			languages: new Set(),
			options: {
				height: 46,
				waveColor: 'gray',
				progressColor: 'green',
				barGap: 0,
				barWidth: 2,
				barRadius: 0,
				duration: 10
			},
			isOverLimit: false,
			isNearLimit: false,
			ttsSpec: null
		}
	},
	computed: {
		tenantAccounts() {
			return this.accounts
				.filter((a) => a.tenant.id === this.selectedTenant)
				.sort((a, b) => a.name.localeCompare(b.name))
		},
		languageVoices() {
			return this.voices.filter((v) => v.language === this.selectedLanguage)
		},
		maxCharacterLimit() {
			if (!this.selectedVoice) return 1000

			const provider = this.ttsSpec?.providers?.[this.selectedVoice.provider]
			return provider?.maxCharacters || 1000
		}
	},
	watch: {
		textInput: {
			handler(newVal) {
				this.isOverLimit = newVal.length > this.maxCharacterLimit
				this.isNearLimit = !this.isOverLimit && newVal.length > this.maxCharacterLimit * 0.9
			},
			immediate: true
		},
		selectedVoice: {
			handler() {
				this.validateTextLength()
			}
		}
	},
	mounted() {
		if (this.selectedTenantId) {
			this.selectedTenant = this.selectedTenantId
			this.hideTenantSelector = true
		} else if (this.tenants?.length === 1) {
			this.selectedTenant = this.tenants[0].id
			this.hideTenantSelector = true
		}

		filesApi
			.getTTSSpec()
			.then((result) => {
				this.ttsSpec = result
				Object.keys(result.providers).map((provider) => {
					result.providers[provider].voices.forEach((voice) => {
						this.languages.add(voice.language)
						this.voices.push({
							provider,
							...voice,
							supportsSpeed: result.providers[provider].supportedOptions?.find((o) => o.id === 'speed')
						})
					})
				})
			})
			.catch(console.error)

		accountsApi
			.getAll()
			.then((result) => {
				this.accounts = result.data.results

				if (this.selectedAccountId && this.selectedTenantId) {
					this.selectedAccount = this.selectedAccountId
					this.hideAccountSelector = true
				}
			})
			.catch(console.error)
	},
	methods: {
		validateTextLength() {
			if (this.textInput) {
				this.isOverLimit = this.textInput.length > this.maxCharacterLimit
				this.isNearLimit = !this.isOverLimit && this.textInput.length > this.maxCharacterLimit * 0.8
			}
		},
		generateTTS() {
			if (!this.selectedTenant) {
				this.$swal.fire({
					icon: 'error',
					title: 'Oops...',
					text: 'Please select a tenant before generating TTS.'
				})
				return
			}

			if (this.isOverLimit) {
				this.$swal.fire({
					icon: 'error',
					title: 'Text Too Long',
					text: `Your text is ${this.textInput.length} characters, but the selected voice only supports up to ${this.maxCharacterLimit} characters. Please shorten your text.`
				})
				return
			}

			if (!this.textInput.trim()) {
				this.$swal.fire({
					icon: 'error',
					title: 'No Text Provided',
					text: 'Please enter some text to convert to speech.'
				})
				return
			}

			this.isLoading = true
			this.isFileGenerated = false
			this.previewedFile = null
			this.file = null
			filesApi
				.generateTTS(this.textInput, this.selectedVoice.provider, this.selectedVoice.name, this.speed)
				.then((result) => {
					this.isFileGenerated = true
					this.previewedFile = result
					this.file = new Blob([this.previewedFile], { type: this.previewedFile.type })
				})
				.catch(console.error)
				.finally(() => {
					this.isLoading = false
				})
		},
		uploadFile() {
			this.isLoading = true
			let formData = new FormData()
			const defaultFileName = `My Recording - ${new Date().toISOString().replace(/[-:.]/g, '').slice(0, 15)}`
			formData.append('file', this.file, `${this.fileName || defaultFileName}.mp3`)

			filesApi[this.selectedAccount ? 'createAccountFile' : 'createTenantFile'](
				this.selectedAccount || this.selectedTenant,
				formData
			)
				.then((result) => {
					this.selectedTenant = null
					this.selectedAccount = null
					this.isFileGenerated = false
					this.previewedFile = null
					this.textInput = ''
					this.fileName = ''
					this.$emit('close', result)
				})
				.catch(console.error)
				.finally(() => {
					this.isLoading = false
				})
		}
	}
}
</script>
